// Monaco, loaded from the vendored AMD build, with typings for the modules
// scripts import.

import { loadScript } from './dom.js';

// The .d.ts files the ifcopenshell packages publish, fetched from the same
// versions the import map loads and laid out as node_modules for Monaco.
const IFCOPENSHELL_TYPES = [
	'index', 'init', 'file', 'entity_instance', 'types',
	'util/index', 'util/inspect', 'util/color',
	'geom/index', 'geom/iterator', 'geom/settings', 'geom/shape',
	'serializers/index',
];
const WASM_TYPES = [ 'index', 'api', 'wasm/ifcopenshell_api' ];

async function addIfcOpenShellTypings( js ) {

	const dist = new URL( './', import.meta.resolve( 'ifcopenshell' ) );
	const wasm = new URL( './', import.meta.resolve( '@ifcopenshell/wasm' ) );
	const fetchText = async ( url ) => {

		const response = await fetch( url );
		if ( ! response.ok ) throw new Error( `${ url }: ${ response.status }` );
		return response.text();

	};

	const files = await Promise.all( [
		...IFCOPENSHELL_TYPES.map( async ( name ) => [
			`file:///node_modules/ifcopenshell/dist/${ name }.d.ts`,
			await fetchText( new URL( `${ name }.d.ts`, dist ) ),
		] ),
		...WASM_TYPES.map( async ( name ) => [
			`file:///node_modules/@ifcopenshell/wasm/${ name }.d.ts`,
			await fetchText( new URL( `${ name }.d.ts`, wasm ) ),
		] ),
	] );
	for ( const [ path, source ] of files ) js.addExtraLib( source, path );

	// Entry points for "ifcopenshell", "ifcopenshell/util" and "ifcopenshell/geom".
	js.addExtraLib( 'export * from "./dist/index";', 'file:///node_modules/ifcopenshell/index.d.ts' );
	js.addExtraLib( 'export * from "../dist/util/index";', 'file:///node_modules/ifcopenshell/util/index.d.ts' );
	js.addExtraLib( 'export * from "../dist/geom/index";', 'file:///node_modules/ifcopenshell/geom/index.d.ts' );

}

/**
 * Monaco's AMD loader sets a global define with define.amd. UMD bundles
 * (Bonsai's, the viewer's Emscripten module) would then register AMD modules
 * instead of their globals, so call this only once those have run.
 *
 * @param {HTMLElement} host
 * @param {{ vs: string, typings: string, ifcopenshellTypes?: boolean }} options
 */
export async function createEditor( host, { vs, typings, ifcopenshellTypes = false } ) {

	await loadScript( `${ vs }/loader.js` );
	const baseUrl = new URL( `${ vs }/../`, location.href ).href;
	window.MonacoEnvironment = {
		getWorkerUrl() {

			const source = `self.MonacoEnvironment = { baseUrl: ${ JSON.stringify( baseUrl ) } };`
				+ `importScripts(${ JSON.stringify( new URL( `${ vs }/base/worker/workerMain.js`, location.href ).href ) });`;
			return URL.createObjectURL( new Blob( [ source ], { type: 'text/javascript' } ) );

		},
	};

	window.require.config( { paths: { vs } } );
	const monaco = await new Promise( ( resolve, reject ) => {

		window.require( [ 'vs/editor/editor.main' ], () => resolve( window.monaco ), reject );

	} );

	const ts = monaco.languages.typescript;
	const js = ts.javascriptDefaults;
	js.setCompilerOptions( {
		target: ts.ScriptTarget.ESNext,
		module: ts.ModuleKind.ESNext,
		moduleResolution: ts.ModuleResolutionKind.NodeJs,
		allowNonTsExtensions: true,
		checkJs: true,
		noLib: false,
		lib: [ 'esnext', 'dom' ],
	} );
	// Scripts are modules; top-level await is fine even without an import.
	js.setDiagnosticsOptions( { diagnosticCodesToIgnore: [ 1375, 1378, 80005 ] } );
	if ( typings ) js.addExtraLib( typings, 'file:///modules.d.ts' );
	if ( ifcopenshellTypes ) addIfcOpenShellTypings( js ).catch( ( error ) => console.warn( 'playground: ifcopenshell typings', error ) );

	monaco.editor.defineTheme( 'aeco-dark', {
		base: 'vs-dark',
		inherit: true,
		rules: [],
		colors: {
			'editor.background': '#151821',
			'editor.lineHighlightBackground': '#1b1f2a',
			'editorLineNumber.foreground': '#4a5163',
			'editorGutter.background': '#151821',
			'editorWidget.background': '#1b1f2a',
		},
	} );

	host.textContent = '';
	const editor = monaco.editor.create( host, {
		theme: 'aeco-dark',
		automaticLayout: true,
		fontSize: 13.5,
		lineHeight: 21,
		fontFamily: 'ui-monospace, "Cascadia Code", "SF Mono", Consolas, monospace',
		minimap: { enabled: false },
		scrollBeyondLastLine: false,
		tabSize: 2,
		padding: { top: 12, bottom: 12 },
		renderLineHighlight: 'line',
		fixedOverflowWidgets: true,
		model: null,
	} );

	return { monaco, editor };

}
